'use client';

import { Identity } from '@/containers/Identities/types';
import { useAddOptOut } from '@/containers/OptOutManager/api';
import { formatPhoneNumber, validatePhoneNumber } from '@/containers/OptOutManager/utils';
import { IdentitySelector } from '@/shared/components/IdentitySelector/IdentitySelector';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { useCallback, useState } from 'react';

interface ManualTabProps {
  accountId: string;
  identities: Identity[];
  showIdentitySelector: boolean;
  onSuccess?: () => void;
  onClose: () => void;
}

export const ManualTab: React.FC<ManualTabProps> = ({
  accountId,
  identities,
  showIdentitySelector,
  onSuccess,
  onClose,
}) => {
  const { showSuccess, showError } = useToast();
  const addOptOutMutation = useAddOptOut();

  const [selectedIdentityId, setSelectedIdentityId] = useState<string>(
    identities.length === 1 ? identities[0].identity_id : ''
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState<{
    phoneNumber?: string;
    identity?: string;
    reason?: string;
  }>({});

  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (showIdentitySelector && !selectedIdentityId) {
      newErrors.identity = 'Please select an identity';
    }

    if (!reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [phoneNumber, selectedIdentityId, reason, showIdentitySelector]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits, spaces, dashes, parentheses
    const cleaned = value.replace(/[^\d\s\-()]/g, '');
    setPhoneNumber(cleaned);

    if (errors.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
    }
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        // Clean phone number - remove all non-digits
        const cleanedPhone = phoneNumber.replace(/\D/g, '');

        await addOptOutMutation.mutateAsync({
          account_id: accountId,
          identity_id: selectedIdentityId || identities[0]?.identity_id || accountId,
          dnc_type: 'PHONE',
          value: cleanedPhone,
          reason: reason.trim(),
        });

        showSuccess(`Successfully added opt-out for ${formatPhoneNumber(cleanedPhone)}`);
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Failed to add opt-out:', error);
        showError('Failed to add opt-out. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      phoneNumber,
      accountId,
      selectedIdentityId,
      identities,
      reason,
      addOptOutMutation,
      showSuccess,
      showError,
      onSuccess,
      onClose,
    ]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Identity Selection - Only show if multiple identities */}
      {showIdentitySelector && (
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Identity</span>
          </label>
          <IdentitySelector
            identities={identities}
            value={selectedIdentityId}
            onChange={(e) => {
              setSelectedIdentityId(e.target.value);
              if (errors.identity) {
                setErrors((prev) => ({ ...prev, identity: undefined }));
              }
            }}
            currentAccountId={accountId}
            className={errors.identity ? 'select-error' : ''}
            placeholder="Select an identity"
          />
          {errors.identity && (
            <label className="label">
              <span className="text-error label-text-alt">{errors.identity}</span>
            </label>
          )}
        </div>
      )}

      {/* Phone Number Input */}
      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Phone Number</span>
        </label>
        <input
          type="tel"
          className={`input input-bordered w-full text-sm ${
            errors.phoneNumber ? 'input-error' : ''
          }`}
          placeholder="(555) 123-4567"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          maxLength={14} // Allow for formatted input
        />
        {errors.phoneNumber && (
          <label className="label">
            <span className="text-error label-text-alt">{errors.phoneNumber}</span>
          </label>
        )}
        <label className="label">
          <span className="label-text-alt text-gray-500">
            Enter a 10-digit US phone number
          </span>
        </label>
      </div>

      {/* Reason Input */}
      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Reason</span>
        </label>
        <textarea
          className={`textarea textarea-bordered w-full text-sm ${
            errors.reason ? 'textarea-error' : ''
          }`}
          placeholder="Enter the reason for this opt-out..."
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (errors.reason) {
              setErrors((prev) => ({ ...prev, reason: undefined }));
            }
          }}
          rows={3}
        />
        {errors.reason && (
          <label className="label">
            <span className="text-error label-text-alt">{errors.reason}</span>
          </label>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
          disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Opt-Out'}
        </button>
      </div>
    </form>
  );
};
